import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Bars3Icon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Button, Select } from '../ui';

function SortableRow({ id, mapping, index, preColumns, postColumns, idColumn, onUpdate, onRemove, onMarkId }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const isId = idColumn?.preKey === mapping.preKey && idColumn?.postKey === mapping.postKey && mapping.preKey && mapping.postKey;

  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-2 px-3 py-2 border border-border rounded-lg bg-background">
      <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1 text-text-muted hover:text-text-primary" aria-label="Drag to reorder">
        <Bars3Icon className="w-4 h-4" />
      </button>
      <span className="text-xs text-text-muted w-6">{index + 1}</span>
      <Select size="sm" value={mapping.preKey || ''} onChange={(e) => onUpdate({ preKey: e.target.value })} className="flex-1">
        <option value="">Pre column...</option>
        {preColumns.filter(c => c.key).map(c => (
          <option key={c.key} value={c.key}>{c.label || c.key}</option>
        ))}
      </Select>
      <span className="text-text-muted text-xs">↔</span>
      <Select size="sm" value={mapping.postKey || ''} onChange={(e) => onUpdate({ postKey: e.target.value })} className="flex-1">
        <option value="">Post column...</option>
        {postColumns.filter(c => c.key).map(c => (
          <option key={c.key} value={c.key}>{c.label || c.key}</option>
        ))}
      </Select>
      <label className="flex items-center gap-1 text-xs cursor-pointer" title="Mark as ID column (joins pre/post responses)">
        <input
          type="radio"
          checked={isId}
          onChange={() => onMarkId(mapping)}
          disabled={!mapping.preKey || !mapping.postKey}
          className="cursor-pointer"
        />
        ID
      </label>
      <button onClick={onRemove} className="p-1 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors">
        <TrashIcon className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

export default function ColumnMappingList({ mappings, setMappings, preColumns, postColumns, idColumn, setIdColumn }) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const items = mappings.map((_, i) => `m-${i}`);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = items.indexOf(active.id);
    const newIdx = items.indexOf(over.id);
    setMappings(arrayMove(mappings, oldIdx, newIdx));
  };

  const updateMapping = (idx, updates) => {
    setMappings(mappings.map((m, i) => i === idx ? { ...m, ...updates } : m));
  };

  const removeMapping = (idx) => {
    const removed = mappings[idx];
    if (idColumn?.preKey === removed.preKey && idColumn?.postKey === removed.postKey) {
      setIdColumn(null);
    }
    setMappings(mappings.filter((_, i) => i !== idx));
  };

  const markAsId = (mapping) => {
    setIdColumn({ preKey: mapping.preKey, postKey: mapping.postKey });
  };

  const addMapping = () => {
    setMappings([...mappings, { preKey: '', postKey: '', label: '' }]);
  };

  return (
    <div className="space-y-2">
      {mappings.length === 0 ? (
        <div className="text-center py-6 text-text-muted text-sm">
          No column mappings defined. Add mappings to pair pre/post columns.
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={items} strategy={verticalListSortingStrategy}>
            <div className="space-y-1.5">
              {mappings.map((mapping, i) => (
                <SortableRow
                  key={`m-${i}`}
                  id={`m-${i}`}
                  mapping={mapping}
                  index={i}
                  preColumns={preColumns}
                  postColumns={postColumns}
                  idColumn={idColumn}
                  onUpdate={(u) => updateMapping(i, u)}
                  onRemove={() => removeMapping(i)}
                  onMarkId={markAsId}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
      <Button variant="secondary" size="sm" onClick={addMapping}>
        <PlusIcon className="w-4 h-4 mr-1 inline" />
        Add mapping
      </Button>
    </div>
  );
}
